import React, { useState, useEffect } from 'react';
import { getAllCentersHealth, getCenterForecast, getCenterHealthScore } from '../../services/centerMembershipService';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

const healthColors = {
  green: { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-700', icon: 'TrendingUp', iconColor: 'text-green-600' },
  yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', icon: 'AlertTriangle', iconColor: 'text-yellow-600' },
  red: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', icon: 'AlertCircle', iconColor: 'text-red-600' },
};

const PredictionDashboard = () => {
  const { userProfile, isSuperAdmin, getUserCenterIds } = useAuth();
  const [centersData, setCentersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [centerDetail, setCenterDetail] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllCentersHealth();
      setCentersData(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load prediction data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCenter = async (center) => {
    setSelectedCenter(center);
    try {
      const [forecast, health] = await Promise.all([
        getCenterForecast(center?.id),
        getCenterHealthScore(center?.id)
      ]);
      setCenterDetail({ forecast, health });
    } catch (err) {
      console.error('Center detail load error:', err);
    }
  };

  const globalStats = {
    totalCenters: centersData?.length || 0,
    greenCount: centersData?.filter(c => c?.health?.health_label === 'green')?.length || 0,
    yellowCount: centersData?.filter(c => c?.health?.health_label === 'yellow')?.length || 0,
    redCount: centersData?.filter(c => c?.health?.health_label === 'red')?.length || 0,
    avgScore: centersData?.length
      ? Math.round(centersData?.reduce((sum, c) => sum + (c?.health?.health_score || 0), 0) / centersData?.length)
      : 0,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Prediction Dashboard V1</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Forecast = booked + weighted pipeline (next 90 days) · Health = occupancy + pipeline gap + expiry risk
        </p>
      </div>
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-destructive text-sm">{error}</div>
      )}
      {/* Global Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{globalStats?.totalCenters}</p>
          <p className="text-xs text-muted-foreground mt-1">Total Centers</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{globalStats?.greenCount}</p>
          <p className="text-xs text-green-600 mt-1">Healthy</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-700">{globalStats?.yellowCount}</p>
          <p className="text-xs text-yellow-600 mt-1">Moderate Risk</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-red-700">{globalStats?.redCount}</p>
          <p className="text-xs text-red-600 mt-1">High Risk</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{globalStats?.avgScore}</p>
          <p className="text-xs text-muted-foreground mt-1">Avg Health Score</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Center Health Cards */}
        <div className="lg:col-span-2 space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Center Health Scores</h2>
          {centersData?.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <Icon name="Building" size={32} className="mx-auto mb-2 text-muted-foreground opacity-40" />
              <p className="text-muted-foreground">No centers found</p>
            </div>
          ) : (
            centersData?.map(center => {
              const label = center?.health?.health_label || 'red';
              const colors = healthColors?.[label] || healthColors?.red;
              const score = center?.health?.health_score || 0;
              const forecast = center?.forecast || {};

              return (
                <div
                  key={center?.id}
                  onClick={() => handleSelectCenter(center)}
                  className={`border rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${
                    selectedCenter?.id === center?.id ? 'ring-2 ring-primary' : ''
                  } ${colors?.bg} ${colors?.border}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm`}>
                        <Icon name={colors?.icon} size={20} className={colors?.iconColor} />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{center?.name}</p>
                        <p className="text-xs text-muted-foreground">{center?.city}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${colors?.badge}`}>
                        {label}
                      </span>
                      <p className="text-lg font-bold text-foreground mt-1">{score}/100</p>
                    </div>
                  </div>
                  {/* Score breakdown */}
                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                    <div className="bg-white/60 rounded-lg p-2 text-center">
                      <p className="font-semibold text-foreground">{center?.health?.occupancy_score || 0}</p>
                      <p className="text-muted-foreground">Occupancy</p>
                    </div>
                    <div className="bg-white/60 rounded-lg p-2 text-center">
                      <p className="font-semibold text-foreground">{center?.health?.pipeline_score || 0}</p>
                      <p className="text-muted-foreground">Pipeline</p>
                    </div>
                    <div className="bg-white/60 rounded-lg p-2 text-center">
                      <p className="font-semibold text-foreground">{center?.health?.expiry_score || 0}</p>
                      <p className="text-muted-foreground">Expiry</p>
                    </div>
                  </div>
                  {/* Forecast */}
                  {forecast?.forecast_seats !== undefined && (
                    <div className="mt-2 pt-2 border-t border-white/40 flex items-center gap-4 text-xs text-muted-foreground">
                      <span>📊 Forecast (90d): <strong className="text-foreground">{forecast?.forecast_seats} seats</strong></span>
                      <span>Booked: <strong className="text-foreground">{forecast?.booked_seats}</strong></span>
                      <span>Pipeline: <strong className="text-foreground">+{forecast?.pipeline_weighted_seats}</strong></span>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Center Detail Panel */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-foreground">Center Detail</h2>
          {!selectedCenter ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <Icon name="MousePointerClick" size={32} className="mx-auto mb-2 text-muted-foreground opacity-40" />
              <p className="text-muted-foreground text-sm">Click a center to see details</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <h3 className="font-semibold text-foreground">{selectedCenter?.name}</h3>

              {centerDetail?.forecast && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">90-Day Forecast</p>
                  <div className="space-y-2">
                    {[
                      { label: 'Currently Booked', value: `${centerDetail?.forecast?.booked_seats || 0} seats / ${centerDetail?.forecast?.booked_sqft || 0} sqft` },
                      { label: 'Weighted Pipeline', value: `+${centerDetail?.forecast?.pipeline_weighted_seats || 0} seats / +${centerDetail?.forecast?.pipeline_weighted_sqft || 0} sqft` },
                      { label: 'Forecast Total', value: `${centerDetail?.forecast?.forecast_seats || 0} seats`, highlight: true },
                      { label: 'Occupancy %', value: `${centerDetail?.forecast?.occupancy_pct || 0}%` },
                      { label: 'Total Capacity', value: `${centerDetail?.forecast?.total_seats || 0} seats` },
                    ]?.map(item => (
                      <div key={item?.label} className={`flex justify-between text-sm ${
                        item?.highlight ? 'font-semibold text-foreground' : 'text-muted-foreground'
                      }`}>
                        <span>{item?.label}</span>
                        <span className={item?.highlight ? 'text-primary' : ''}>{item?.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {centerDetail?.health && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Health Breakdown</p>
                  <div className="space-y-2">
                    {[
                      { label: 'Health Score', value: `${centerDetail?.health?.health_score || 0}/100`, highlight: true },
                      { label: 'Status', value: centerDetail?.health?.health_label || 'N/A' },
                      { label: 'Active Pipeline Leads', value: centerDetail?.health?.pipeline_leads || 0 },
                      { label: 'Expiring in 90 days', value: centerDetail?.health?.expiring_soon || 0 },
                    ]?.map(item => (
                      <div key={item?.label} className={`flex justify-between text-sm ${
                        item?.highlight ? 'font-semibold text-foreground' : 'text-muted-foreground'
                      }`}>
                        <span>{item?.label}</span>
                        <span className={item?.highlight ? 'text-primary capitalize' : 'capitalize'}>{item?.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Phase 2 Placeholder */}
          <div className="bg-muted/30 border border-dashed border-border rounded-xl p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Phase 2 – Coming Soon</p>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p className="flex items-center gap-1"><Icon name="Facebook" size={12} /> Facebook Lead Ads connector</p>
              <p className="flex items-center gap-1"><Icon name="Globe" size={12} /> Google Lead Forms connector</p>
              <p className="flex items-center gap-1"><Icon name="Webhook" size={12} /> Aggregator API integration</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictionDashboard;
